import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { ProgressTabs } from '@/components/ProgressTabs';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { HeaderButtons } from '@/pages/avatar/PremiumPlusCreatePage/components/Header/HeaderButtons';
import theme from '@/theme';
import { AppPathname, AppRouteNames, ExtendedLocation } from '@/utils/types';
import styled from '@emotion/styled';
import { IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreateConfirmModal } from '../../CreateConfirmModal';
import { LeaveConfirmationModal } from '../LeaveConfirmationModal';

const HeaderContainer = styled.header`
    width: 100%;
    display: flex;
    background: ${theme.colors.white};
    align-items: center;
    justify-content: space-between;
    border-bottom: ${theme.colors.black[200]} solid 1px;
    min-height: 96px;
    padding: 0 32px;
    position: relative;
    box-shadow: 0px 1px 2px 0px rgba(187, 187, 187, 0.12);
`;

const StyledBackButton = styled(IconButton)`
    gap: 10px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const BackText = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 500;
`;

const StyledProgressTabs = styled(ProgressTabs)`
    position: absolute;
    width: 30%;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: column;
    margin-top: 8px;
    left: 50%;
    transform: translate(-50%, 0);
`;

export const Header = observer(() => {
    const navigate = useNavigate();
    const location = useLocation() as ExtendedLocation;

    const createAvatarStore = useCreateAvatarStore();

    const [leaveConfirmationModal, setLeaveConfirmationModal] = useState(false);
    const [createConfirmationModal, setCreateConfirmationModal] = useState(false);

    const handleLeave = () => {
        setLeaveConfirmationModal(false);
        navigate(-1);
    };

    const handleBack = () => {
        if (createAvatarStore.isDirty) {
            return setLeaveConfirmationModal(true);
        }

        handleLeave();
    };

    const handleCancel = () => {
        createAvatarStore.resetUploads();
        handleLeave();
    };

    const onCloseConfirmationModal = () => {
        setCreateConfirmationModal(false);
    };

    const onConfirmClickedModal = () => {
        createAvatarStore.setTab(createAvatarStore.tab + 1);
        setCreateConfirmationModal(false);
    };

    // should only be null if someone forgets to set the from state
    const prevPageName = location.state?.fromPath ? AppRouteNames[location.state.fromPath as AppPathname] : '';

    return (
        <>
            <HeaderContainer>
                <StyledBackButton onClick={handleBack}>
                    <>
                        <StyledMaskedIcon src={ArrowLeft} size="32px" />
                        <BackText variant="h4">{prevPageName}</BackText>
                    </>
                </StyledBackButton>
                <StyledProgressTabs
                    tab={createAvatarStore.tab}
                    setTab={(tab: number) => createAvatarStore.setTab(tab)}
                    tabs={createAvatarStore.tabs}
                />
                <HeaderButtons setCreateConfirmationModal={setCreateConfirmationModal} />
            </HeaderContainer>
            <LeaveConfirmationModal
                onClose={() => setLeaveConfirmationModal(false)}
                onCancel={handleCancel}
                open={leaveConfirmationModal}
            />
            <CreateConfirmModal
                onClose={() => onCloseConfirmationModal()}
                open={createConfirmationModal}
                onConfirm={onConfirmClickedModal}
            />
        </>
    );
});
